import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Textarea } from './ui/textarea';
import { ArrowRight, Check, Zap } from 'lucide-react';

const countryCodes = [
  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '+44', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+91', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: '+86', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '+81', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: '+49', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: '+33', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '+61', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: '+55', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: '+971', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+966', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: '+92', flag: '\u{1F1F5}\u{1F1F0}' },
  { code: '+880', flag: '\u{1F1E7}\u{1F1E9}' },
  { code: '+234', flag: '\u{1F1F3}\u{1F1EC}' },
  { code: '+82', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: '+39', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: '+34', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: '+7', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: '+52', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: '+62', flag: '\u{1F1EE}\u{1F1E9}' },
  { code: '+90', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: '+27', flag: '\u{1F1FF}\u{1F1E6}' },
  { code: '+63', flag: '\u{1F1F5}\u{1F1ED}' },
  { code: '+84', flag: '\u{1F1FB}\u{1F1F3}' },
  { code: '+66', flag: '\u{1F1F9}\u{1F1ED}' },
  { code: '+60', flag: '\u{1F1F2}\u{1F1FE}' },
  { code: '+20', flag: '\u{1F1EA}\u{1F1EC}' },
  { code: '+48', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: '+31', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: '+46', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: '+41', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: '+65', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: '+64', flag: '\u{1F1F3}\u{1F1FF}' },
  { code: '+353', flag: '\u{1F1EE}\u{1F1EA}' },
  { code: '+972', flag: '\u{1F1EE}\u{1F1F1}' },
  { code: '+254', flag: '\u{1F1F0}\u{1F1EA}' },
  { code: '+233', flag: '\u{1F1EC}\u{1F1ED}' },
  { code: '+57', flag: '\u{1F1E8}\u{1F1F4}' },
  { code: '+56', flag: '\u{1F1E8}\u{1F1F1}' },
  { code: '+54', flag: '\u{1F1E6}\u{1F1F7}' },
];

const HeroBanner = ({ onOpenContactForm }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const ne = { email: '', phone: '' };
    let err = false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { ne.email = 'Valid email required'; err = true; }
    if (formData.phone.replace(/\D/g, '').length !== 10) { ne.phone = 'Must be 10 digits'; err = true; }
    if (err) { setErrors(ne); return; }
    toast({ title: "Request Received", description: "Our team will contact you within 24 hours." });
    setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
  };

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center pt-24 pb-20 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#3B82F6]/[0.07] rounded-full blur-[120px]" />
        <div className="absolute bottom-20 left-20 w-[400px] h-[400px] bg-[#06B6D4]/[0.05] rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          <motion.div className="lg:col-span-7 space-y-7" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#3B82F6]/10 border border-[#3B82F6]/20">
              <Zap className="w-3.5 h-3.5 text-[#3B82F6]" />
              <span className="text-xs font-semibold text-[#3B82F6] uppercase tracking-wider">US Trademark Registration</span>
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-[68px] font-semibold tracking-tight leading-[1.05]">
              <span className="text-white">Trademark Protection</span><br />
              <span className="text-white">for Logos </span>
              <span className="text-gradient">in the US</span>
            </h1>

            <p className="text-lg text-[#A1A1AA] leading-relaxed max-w-xl">
              Protect your logo, secure your brand identity, and gain legal ownership with professional USPTO trademark filing services.
            </p>

            <div className="space-y-3">
              {[
                'USPTO trademark filing & approval support',
                'Logo trademark search & availability check',
                'End-to-end trademark protection services',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#3B82F6]/15 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-[#3B82F6]" />
                  </div>
                  <span className="text-[#A1A1AA]">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button data-testid="hero-cta-search" onClick={onOpenContactForm}
                className="btn-gradient text-white font-semibold px-7 py-3 rounded-lg border-0 text-base">
                Check Logo Availability <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button data-testid="hero-cta-protect" onClick={onOpenContactForm}
                variant="outline"
                className="border-white/[0.15] text-white hover:bg-white/[0.05] font-semibold px-7 py-3 rounded-lg text-base">
                Protect My Logo Now
              </Button>
            </div>
          </motion.div>

          <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <div className="glass-card rounded-2xl p-7">
              <div className="mb-5">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#06B6D4] uppercase tracking-wider mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#06B6D4]" /> Free Consultation
                </div>
                <h3 className="font-heading text-xl font-semibold text-white">Start Your Logo Trademark</h3>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3.5">
                <div>
                  <Label className="text-[#A1A1AA] font-medium text-xs mb-1.5 block">Full Name *</Label>
                  <Input data-testid="hero-name-input" placeholder="Your full name" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                    className="h-10 bg-white/[0.05] border-white/[0.08] rounded-lg text-white placeholder:text-[#52525B] focus:border-[#3B82F6]/50 focus:ring-1 focus:ring-[#3B82F6]/30" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA] font-medium text-xs mb-1.5 block">Email *</Label>
                  <Input data-testid="hero-email-input" type="email" placeholder="you@company.com" value={formData.email}
                    onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setErrors({ ...errors, email: '' }); }} required
                    className={`h-10 bg-white/[0.05] rounded-lg text-white placeholder:text-[#52525B] focus:ring-1 focus:ring-[#3B82F6]/30 ${errors.email ? 'border-red-500' : 'border-white/[0.08] focus:border-[#3B82F6]/50'}`} />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label className="text-[#A1A1AA] font-medium text-xs mb-1.5 block">Phone *</Label>
                  <div className="flex gap-2">
                    <Select value={formData.countryCode} onValueChange={(v) => setFormData({ ...formData, countryCode: v })}>
                      <SelectTrigger data-testid="hero-country-select" className="w-[90px] h-10 bg-white/[0.05] border-white/[0.08] rounded-lg text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#18181B] border-[#27272A] max-h-[250px]">
                        {countryCodes.map((c) => (<SelectItem key={c.code} value={c.code}><span className="flex items-center gap-1.5">{c.flag} {c.code}</span></SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Input data-testid="hero-phone-input" type="tel" placeholder="1234567890" value={formData.phone}
                      onChange={(e) => { const d = e.target.value.replace(/\D/g, ''); if (d.length <= 10) setFormData({ ...formData, phone: d }); setErrors({ ...errors, phone: '' }); }}
                      required maxLength={10}
                      className={`flex-1 h-10 bg-white/[0.05] rounded-lg text-white placeholder:text-[#52525B] focus:ring-1 focus:ring-[#3B82F6]/30 ${errors.phone ? 'border-red-500' : 'border-white/[0.08] focus:border-[#3B82F6]/50'}`} />
                  </div>
                  {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <Label className="text-[#A1A1AA] font-medium text-xs mb-1.5 block">Description</Label>
                  <Textarea data-testid="hero-description-input" placeholder="What are you looking to protect?" rows={2} value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-white/[0.05] border-white/[0.08] rounded-lg text-white placeholder:text-[#52525B] focus:border-[#3B82F6]/50 focus:ring-1 focus:ring-[#3B82F6]/30 resize-none" />
                </div>
                <Button data-testid="hero-submit-btn" type="submit" className="w-full btn-gradient text-white font-semibold h-11 rounded-lg border-0">
                  Check Logo Availability <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-xs text-[#52525B] text-center">Free consultation. No obligation.</p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
